import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Coordinate, ElementType } from '../types';
import { photosApi, projectsApi } from '../lib/api';

// Camera type
export type CameraType = 'FRONTE' | 'RETRO' | 'UNKNOWN';
export type CameraFilter = 'FRONTE' | 'RETRO';

// Photo in the system (now loaded from server)
export interface Photo {
  id: string;
  projectId: string;
  filename: string;
  width: number;
  height: number;
  
  // GPS coordinates
  gps: Coordinate | null;
  
  // Element binding
  elementId: string | null;
  elementType: ElementType | null;
  
  // Date taken from EXIF
  dateTaken: Date | null;
  
  // Metadata
  fileSize: number;
  
  // Camera type (FRONTE/RETRO)
  camera: CameraType;
  
  // Cached URLs (in memory)
  thumbnailUrl?: string;
  fullUrl?: string;
  
  // Status
  isProcessing: boolean;
  addedAt: Date;
  
  // Review status
  isReviewed: boolean;
  reviewedAt: Date | null;
}

interface PhotoStore {
  // Data
  photos: Photo[];
  currentProjectId: string | null;
  
  // Camera filter
  cameraFilter: CameraFilter;
  
  // Loading state
  isLoading: boolean;
  loadingProgress: number;
  
  // Project management
  setCurrentProject: (projectId: string) => void;
  loadPhotosFromServer: (projectId: string) => Promise<void>;
  
  // Photo URL management
  getPhotoUrl: (photoId: string, thumbnail?: boolean) => Promise<string | null>;
  preloadThumbnails: (photoIds: string[]) => Promise<void>;
  
  // Camera filter
  setCameraFilter: (filter: CameraFilter) => void;
  
  // Binding
  bindPhotoToElement: (photoId: string, elementId: string, elementType: ElementType) => Promise<void>;
  unbindPhoto: (photoId: string) => Promise<void>;
  
  // Review status
  updatePhotoReviewStatus: (photoId: string, isReviewed: boolean) => void;
  
  // Selectors
  getPhotoById: (id: string) => Photo | undefined;
  getPhotosByElement: (elementId: string) => Photo[];
  getUnboundPhotos: () => Photo[];
  getPhotosWithGps: () => Photo[];
  getPhotosCount: () => number;
  getFilteredPhotos: () => Photo[];
  getPhotosByCamera: (camera: CameraType) => Photo[];
}

export const usePhotoStore = create<PhotoStore>()(
  persist(
    (set, get) => ({
      photos: [],
      currentProjectId: null,
      cameraFilter: 'FRONTE' as CameraFilter,
      isLoading: false,
      loadingProgress: 0,

      setCurrentProject: (projectId: string) => {
        set({ currentProjectId: projectId, photos: [] });
      },

      loadPhotosFromServer: async (projectId: string) => {
        set({ isLoading: true, loadingProgress: 0 });

        try {
          // Load photos with thumbnail URLs from server
          const response = await projectsApi.getPhotos(projectId, {
            page: 1,
            limit: 1000, // Adjust based on expected photo count
            withUrls: true,
            thumbnail: true,
          });

          const serverPhotos = response.data.data;

          // Convert server photos to store format
          const photos: Photo[] = serverPhotos.map((sp: any) => ({
            id: sp.id,
            projectId: sp.projectId,
            filename: sp.filename,
            width: sp.width || 0,
            height: sp.height || 0,
            gps: sp.lat && sp.lng ? { latitude: sp.lat, longitude: sp.lng } : null,
            elementId: sp.elementId || null,
            elementType: sp.element?.type || null,
            dateTaken: sp.takenAt ? new Date(sp.takenAt) : null,
            fileSize: sp.sizeBytes || 0,
            camera: sp.camera || 'FRONTE',
            thumbnailUrl: sp.url || undefined, // Already has signed URL from server
            isProcessing: false,
            addedAt: sp.createdAt ? new Date(sp.createdAt) : new Date(),
            isReviewed: sp.isReviewed || false,
            reviewedAt: sp.reviewedAt ? new Date(sp.reviewedAt) : null,
          }));

          set({ photos, currentProjectId: projectId, isLoading: false });
          console.log(`✅ Loaded ${photos.length} photos from server for project ${projectId}`);
        } catch (error) {
          console.error('Failed to load photos from server:', error);
          set({ photos: [], isLoading: false });
        }
      },

      getPhotoUrl: async (photoId: string, thumbnail = true) => {
        const photo = get().photos.find((p) => p.id === photoId);
        if (!photo) {
          console.warn(`Photo ${photoId} not found in store`);
          return null;
        }

        // Check cache
        if (thumbnail && photo.thumbnailUrl) {
          return photo.thumbnailUrl;
        }
        if (!thumbnail && photo.fullUrl) {
          return photo.fullUrl;
        }

        // Request signed URL from server
        try {
          const response = await photosApi.getUrl(photoId, thumbnail);
          const url = response.data.data.url;

          // Cache URL in store
          set((state) => ({
            photos: state.photos.map((p) =>
              p.id === photoId
                ? { ...p, [thumbnail ? 'thumbnailUrl' : 'fullUrl']: url }
                : p
            ),
          }));

          return url;
        } catch (error) {
          console.error(`Failed to get URL for photo ${photoId}:`, error);
          return null;
        }
      },

      preloadThumbnails: async (photoIds: string[]) => {
        const BATCH_SIZE = 20;

        for (let i = 0; i < photoIds.length; i += BATCH_SIZE) {
          const batch = photoIds.slice(i, Math.min(i + BATCH_SIZE, photoIds.length));

          try {
            // Use batch endpoint for efficiency
            const response = await photosApi.batchUrls(batch, true);
            const photosWithUrls = response.data.data.photos;

            // Update store with URLs
            set((state) => ({
              photos: state.photos.map((p) => {
                const photoWithUrl = photosWithUrls.find((pw: any) => pw.id === p.id);
                if (photoWithUrl && photoWithUrl.url) {
                  return { ...p, thumbnailUrl: photoWithUrl.url };
                }
                return p;
              }),
            }));
          } catch (error) {
            console.error(`Failed to preload thumbnails batch ${i / BATCH_SIZE + 1}:`, error);
          }
        }
      },

      setCameraFilter: (filter: CameraFilter) => set({ cameraFilter: filter }),

      bindPhotoToElement: async (photoId, elementId, elementType) => {
        // Update in store immediately (optimistic update)
        set((state) => ({
          photos: state.photos.map((p) =>
            p.id === photoId ? { ...p, elementId, elementType } : p
          ),
        }));

        // Update on server
        try {
          await photosApi.update(photoId, { elementId });
        } catch (error) {
          console.error('Failed to update photo binding on server:', error);
          // Revert on error
          set((state) => ({
            photos: state.photos.map((p) =>
              p.id === photoId ? { ...p, elementId: null, elementType: null } : p
            ),
          }));
        }
      },

      unbindPhoto: async (photoId) => {
        // Update in store immediately (optimistic update)
        set((state) => ({
          photos: state.photos.map((p) =>
            p.id === photoId ? { ...p, elementId: null, elementType: null } : p
          ),
        }));

        // Update on server
        try {
          await photosApi.update(photoId, { elementId: null });
        } catch (error) {
          console.error('Failed to unbind photo on server:', error);
        }
      },

      updatePhotoReviewStatus: (photoId, isReviewed) => {
        set((state) => ({
          photos: state.photos.map((p) =>
            p.id === photoId ? { ...p, isReviewed, reviewedAt: isReviewed ? new Date() : null } : p
          ),
        }));
      },

      getPhotoById: (id) => get().photos.find((p) => p.id === id),

      getPhotosByElement: (elementId) =>
        get().photos.filter((p) => p.elementId === elementId),

      getUnboundPhotos: () => get().photos.filter((p) => !p.elementId),

      getPhotosWithGps: () => get().photos.filter((p) => p.gps !== null),

      getPhotosCount: () => get().photos.length,

      getFilteredPhotos: () => {
        const { photos } = get();
        const photoList = photos || [];

        // Sort photos in pairs: FRONTE₁, RETRO₁, FRONTE₂, RETRO₂...
        return photoList.sort((a, b) => {
          // Extract timestamp from filename
          const getTimestamp = (filename: string): string => {
            const match = filename.match(/(\d{4}_\d{2}_\d{2}@\d{2}\.\d{2}\.\d{2})/);
            return match ? match[1] : filename;
          };

          const timeA = getTimestamp(a.filename);
          const timeB = getTimestamp(b.filename);

          // First sort by timestamp
          if (timeA !== timeB) {
            return timeA.localeCompare(timeB);
          }

          // Same timestamp - FRONTE first, then RETRO
          if (a.camera === 'FRONTE' && b.camera === 'RETRO') return -1;
          if (a.camera === 'RETRO' && b.camera === 'FRONTE') return 1;

          return 0;
        });
      },

      getPhotosByCamera: (camera: CameraType) => {
        const filtered = get().photos.filter((p) => p.camera === camera);
        return filtered.sort((a, b) => {
          if (a.dateTaken && b.dateTaken) {
            return new Date(a.dateTaken).getTime() - new Date(b.dateTaken).getTime();
          }
          return a.filename.localeCompare(b.filename);
        });
      },
    }),
    {
      name: 'elifly-photos',
      // Only persist camera filter, not photos (they come from server)
      partialize: (state) => ({
        cameraFilter: state.cameraFilter,
      }),
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          try {
            return JSON.parse(str);
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
